<template>
  <Select
    v-model="selectedLanguage"
    :options="languageOptions"
    option-label="label"
    option-value="value"
    placeholder=""
    class="h-10 language-switcher"
    :pt="{
      root: { class: 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 pl-2' },
      input: {
        class:
          'text-base py-0 px-1 text-center bg-white dark:bg-gray-800 text-gray-900 dark:text-white'
      }
    }"
    @update:model-value="handleLanguageChange"
  >
    <template #value="slotProps">
      <span v-if="slotProps.value" class="language-flag-value">
        <img
          v-if="getFlagForLocale(slotProps.value)"
          :src="getFlagForLocale(slotProps.value)"
          :alt="slotProps.value"
          class="flag-icon"
        />
        <span v-else class="text-sm font-semibold uppercase">{{ slotProps.value }}</span>
      </span>
    </template>
    <template #option="slotProps">
      <span class="language-flag-option">
        <img
          v-if="slotProps.option.flag"
          :src="slotProps.option.flag"
          :alt="slotProps.option.label"
          class="flag-icon"
        />
        <span>{{ slotProps.option.label }}</span>
      </span>
    </template>
  </Select>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import Select from "primevue/select";
import { useLanguageStore } from "../stores/language";
import { languagePickerOptions } from "../i18n/languageOptions.js";

const props = defineProps({
  availableLocales: {
    type: Array,
    default: null
  }
});

const languageOptions = computed(() => {
  if (!props.availableLocales || props.availableLocales.length === 0) {
    return languagePickerOptions;
  }
  return languagePickerOptions.filter((option) =>
    props.availableLocales.includes(option.value)
  );
});

const languageStore = useLanguageStore();

const selectedLanguage = ref(languageStore.currentLocale);

watch(
  () => languageStore.currentLocale,
  (newLocale) => {
    selectedLanguage.value = newLocale;
  }
);

const handleLanguageChange = (value) => {
  if (value) languageStore.setLocale(value);
};

const defaultFlag = computed(() => languageOptions.value[0]?.flag);

const getFlagForLocale = (locale) => {
  const option = languageOptions.value.find((opt) => opt.value === locale);
  return option?.flag ?? defaultFlag.value;
};
</script>

<style>
/* Strip internal padding so the flag can fill the space */
.language-switcher .p-select-label {
  text-overflow: clip !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.language-switcher .p-select-dropdown {
  transform: scale(0.7);
  width: 1.5rem !important;
  padding: 0 !important;
}

.p-select-option {
  justify-content: center;
}

/* Flag icon sizing */
.flag-icon {
  width: 24px;
  height: 20px;
  object-fit: contain;
  display: block;
}

/* Language switcher value and option styling */
.language-flag-value {
  display: flex;
  align-items: center;
  justify-content: center;
}

.language-flag-option {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 500;
}
</style>
